import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { findEstimate } from '@utils/api/find';

// Wait for what the next search would run to settle (the map, typing) before asking what it costs;
// as the plan preview does
const ESTIMATE_DEBOUNCE_MS = 350;

/**
 * What the next search would cost (`POST /find/estimate`), asked again once what it would run
 * settles: the area, the words or examples, the model, compute's state (starting it costs too).
 * Null while unknown, and when asking failed: the Find button then shows no cost, and still finds.
 * @param {Object} params
 * @param {string} [params.projectUuid]
 * @param {boolean} params.enabled
 * @param {Object|null} params.body - From `estimateBody`, or null when nothing would run
 * @param {string} [params.worker] - Compute's state
 * @returns {Object|null} `{ plan, estimate }`
 */
export const useFindEstimate = ({ projectUuid, enabled, body, worker }) => {
	const key = body ? JSON.stringify(body) : null;
	const [settled, setSettled] = useState(key);
	useEffect(() => {
		const timer = setTimeout(() => setSettled(key), ESTIMATE_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [key]);
	const estimate = useQuery({
		queryKey: ['find', 'estimate', projectUuid, settled, worker],
		queryFn: () => findEstimate({ projectUuid, body: JSON.parse(settled) }),
		enabled: Boolean(enabled && projectUuid && settled),
		staleTime: 30000,
		retry: false,
		placeholderData: keepPreviousData,
	});
	if (!enabled || !key || estimate.isError || !estimate.data?.estimate) return null;
	return estimate.data;
};
